import React, { useState, useEffect } from 'react';
import {
  Stack,
  Title,
  Button,
  Group,
  Table,
  Badge,
  ActionIcon,
  Paper,
  Text,
  Modal,
  TextInput,
  Pagination,
  Loader,
  Center,
  Grid,
  Card
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconEye,
  IconTrash,
  IconSearch,
  IconRefresh,
  IconBook,
  IconSchool,
  IconStar,
  IconStarFilled,
  IconList,
  IconExternalLink
} from '@tabler/icons-react';
import { curriculumService } from '../../services/curriculumService';
import CurriculumForm from '../../components/admin/CurriculumForm';
import CurriculumDetails from '../../components/admin/CurriculumDetails';
import CurriculumBuilder from '../../components/admin/CurriculumBuilder';

const CurriculumManager = () => {
  const [curriculums, setCurriculums] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCurriculum, setSelectedCurriculum] = useState(null);
  const [formMode, setFormMode] = useState('create');
  
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [formOpened, formHandlers] = useDisclosure(false);
  const [detailsOpened, detailsHandlers] = useDisclosure(false);
  const [builderOpened, builderHandlers] = useDisclosure(false);
  const [deleteOpened, deleteHandlers] = useDisclosure(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadCurriculums();
  }, [debouncedSearch, currentPage]);

  const loadInitialData = async () => {
    try {
      const statsData = await curriculumService.getStats();
      setStats(statsData);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load statistics',
        color: 'red',
      });
    }
  };

  const loadCurriculums = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 20,
        ...(debouncedSearch && { search: debouncedSearch }),
      };
      
      const data = await curriculumService.getCurriculums(params);
      setCurriculums(data.data);
      setTotalPages(data.last_page);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load curriculums',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedCurriculum(null);
    setFormMode('create');
    formHandlers.open();
  };

  const handleEdit = (curriculum) => {
    setSelectedCurriculum(curriculum);
    setFormMode('edit');
    formHandlers.open();
  };

  const handleView = (curriculum) => {
    setSelectedCurriculum(curriculum);
    detailsHandlers.open();
  };

  const handleBuilder = (curriculum) => {
    setSelectedCurriculum(curriculum);
    builderHandlers.open();
  };

  const handleDelete = (curriculum) => {
    setSelectedCurriculum(curriculum);
    deleteHandlers.open();
  };

  const handlePreview = async (curriculum) => {
    try {
      const previewData = await curriculumService.previewCurriculum(curriculum.id);
      // Open preview in new tab
      const previewWindow = window.open('', '_blank');
      previewWindow.document.write(generatePreviewHTML(previewData));
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate preview',
        color: 'red',
      });
    }
  };

  const handleSetDefault = async (curriculum) => {
    try {
      await curriculumService.setDefault(curriculum.id);
      notifications.show({
        title: 'Success',
        message: `${curriculum.title} set as default curriculum`,
        color: 'green',
      });
      loadCurriculums();
      loadInitialData();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to set default curriculum',
        color: 'red',
      });
    }
  };

  const confirmDelete = async () => {
    try {
      await curriculumService.deleteCurriculum(selectedCurriculum.id);
      notifications.show({
        title: 'Success',
        message: 'Curriculum deleted successfully',
        color: 'green',
      });
      deleteHandlers.close();
      setSelectedCurriculum(null);
      loadCurriculums();
      loadInitialData();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete curriculum';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    }
  };

  const handleFormSubmit = () => {
    formHandlers.close();
    setSelectedCurriculum(null);
    loadCurriculums();
    loadInitialData();
  };

  const handleBuilderSubmit = () => {
    builderHandlers.close();
    setSelectedCurriculum(null);
    loadCurriculums();
  };

  const generatePreviewHTML = (previewData) => {
    const { curriculum, schedule, sample_start_time } = previewData;
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Curriculum Preview - ${curriculum.title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { background: #228be6; color: white; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
            .class-item { border: 1px solid #ddd; padding: 15px; margin: 10px 0; border-radius: 8px; }
            .badge { background: #e3f2fd; color: #1976d2; padding: 4px 8px; border-radius: 4px; font-size: 12px; }
            .time { color: #666; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Curriculum Preview: ${curriculum.title}</h1>
            <p>Sample Start Time: ${new Date(sample_start_time).toLocaleString('en-IN')}</p>
          </div>
          
          <h2>Class Schedule</h2>
          ${schedule.map((item, index) => `
            <div class="class-item">
              <h3>Class ${index + 1}: ${item.class.title}</h3>
              <p><span class="badge">${item.class.type === 'video_class' ? 'Video Class' : 'Live Class'}</span></p>
              <p>${item.class.short_description}</p>
              <p class="time">Available: ${item.available_at_formatted}</p>
              <p class="time">Schedule: ${item.schedule_time} (+${item.plus_days} days)</p>
            </div>
          `).join('')}
        </body>
      </html>
    `;
  };

  const clearFilters = () => {
    setSearchValue('');
    setCurrentPage(1);
  };

  const getDefaultBadge = (isDefault) => {
    return isDefault 
      ? <Badge color="gold" leftSection={<IconStarFilled size={12} />}>Default</Badge>
      : null;
  };

  const StatCard = ({ icon: Icon, title, value, color = 'blue' }) => (
    <Card withBorder p="md">
      <Group>
        <Icon size={24} color={`var(--mantine-color-${color}-6)`} />
        <Stack gap="xs">
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text size="lg" fw={700}>
            {value}
          </Text>
        </Stack>
      </Group>
    </Card>
  );

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={1}>Curriculum Manager</Title>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => {
              loadCurriculums();
              loadInitialData();
            }}
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreate}
          >
            Create Curriculum
          </Button>
        </Group>
      </Group>

      {/* Stats Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconBook}
            title="Total Curriculums"
            value={stats.total_curriculums || 0}
            color="blue"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconStar}
            title="Default Curriculum"
            value={stats.default_curriculum || 'None'}
            color="gold"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconList}
            title="With Classes"
            value={stats.curriculums_with_classes || 0}
            color="green"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={IconSchool}
            title="In Use"
            value={stats.curriculums_in_use || 0}
            color="purple"
          />
        </Grid.Col>
      </Grid>

      {/* Filters */}
      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Search by title..."
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ flex: 1 }}
          />
          <Button variant="light" onClick={clearFilters}>
            Clear
          </Button>
        </Group>
      </Paper>

      {/* Curriculums Table */}
      {loading ? (
        <Center h={400}>
          <Loader size="lg" />
        </Center>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>Classes</Table.Th>
                <Table.Th>Batches</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {curriculums.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Center p="xl">
                      <Stack align="center">
                        <IconBook size={48} color="gray" />
                        <Text c="dimmed">No curriculums found</Text>
                        <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
                          Create First Curriculum
                        </Button>
                      </Stack>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : (
                curriculums.map((curriculum) => (
                  <Table.Tr key={curriculum.id}>
                    <Table.Td>
                      <Group>
                        <Text fw={500}>{curriculum.title}</Text>
                        {getDefaultBadge(curriculum.is_default)}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="blue">
                        {curriculum.classes_count || 0} classes
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="purple">
                        {curriculum.batches_count || 0} batches
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {curriculum.classes_count > 0 ? (
                        <Badge color="green">Ready</Badge>
                      ) : (
                        <Badge color="gray">No Classes</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {new Date(curriculum.created_at).toLocaleDateString('en-IN')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon
                          variant="light"
                          size="sm"
                          onClick={() => handleView(curriculum)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          size="sm"
                          onClick={() => handleEdit(curriculum)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="green"
                          size="sm"
                          onClick={() => handleBuilder(curriculum)}
                        >
                          <IconList size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="purple"
                          size="sm"
                          onClick={() => handlePreview(curriculum)}
                        >
                          <IconExternalLink size={16} />
                        </ActionIcon>
                        {!curriculum.is_default && (
                          <ActionIcon
                            variant="light"
                            color="gold"
                            size="sm"
                            onClick={() => handleSetDefault(curriculum)}
                          >
                            <IconStar size={16} />
                          </ActionIcon>
                        )}
                        <ActionIcon
                          variant="light"
                          color="red"
                          size="sm"
                          onClick={() => handleDelete(curriculum)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>

          {totalPages > 1 && (
            <Group justify="center" p="md">
              <Pagination
                value={currentPage}
                onChange={setCurrentPage}
                total={totalPages}
              />
            </Group>
          )}
        </Paper>
      )}

      {/* Modals */}
      <Modal
        opened={formOpened}
        onClose={() => {
          formHandlers.close();
          setSelectedCurriculum(null);
        }}
        title={formMode === 'create' ? 'Create New Curriculum' : 'Edit Curriculum'}
        size="md"
      >
        <CurriculumForm
          curriculum={selectedCurriculum}
          mode={formMode}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            formHandlers.close();
            setSelectedCurriculum(null);
          }}
        />
      </Modal>

      <Modal
        opened={detailsOpened}
        onClose={detailsHandlers.close}
        title="Curriculum Details"
        size="xl"
      >
        {selectedCurriculum && (
          <CurriculumDetails curriculum={selectedCurriculum} />
        )}
      </Modal>

      <Modal
        opened={builderOpened}
        onClose={builderHandlers.close}
        title="Curriculum Builder"
        size="95%"
        fullScreen={window.innerWidth < 768}
      >
        {selectedCurriculum && (
          <CurriculumBuilder
            curriculum={selectedCurriculum}
            onSubmit={handleBuilderSubmit}
            onCancel={builderHandlers.close}
          />
        )}
      </Modal>

      <Modal
        opened={deleteOpened}
        onClose={deleteHandlers.close}
        title="Confirm Delete"
      >
        <Stack>
          <Text>
            Are you sure you want to delete curriculum "{selectedCurriculum?.title}"? 
            This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="light" onClick={deleteHandlers.close}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDelete}>
              Delete Curriculum
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default CurriculumManager;